import { useEffect, useState } from "react";

// A number <input> you can actually TYPE in freely.
//
// The problem this solves: a normal controlled number input that clamps /
// parses on every keystroke (e.g. onChange={(e) => setX(Math.max(0, parseInt(e.target.value)))})
// fights the user — it won't let you clear the box, jumps to min/max mid-typing,
// and turns an empty field into 0. This component keeps a local draft string so
// you can clear it and type whatever you want, and only clamps + commits the
// final value on blur or Enter (matching the Bump Studio fields).
//
// Contract: `value` is a NUMBER, `onChange` is called with a clamped NUMBER.
// For fields that display dollars but store cents, do the /100 and *100 in the
// parent (value={cents/100}, onChange={(d) => setCents(d*100)}).
export function FreeNumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  precision = 0,
  className = "",
  ...rest
}) {
  const round = (n) => {
    if (precision <= 0) return Math.round(n);
    const f = Math.pow(10, precision);
    return Math.round(n * f) / f;
  };
  const fmt = (v) =>
    v === null || v === undefined || v === ""
      ? ""
      : precision > 0
        ? Number(v).toFixed(precision)
        : String(v);

  const [draft, setDraft] = useState(fmt(value));
  // Re-sync if the value changes from elsewhere (reset, load, etc.).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft(fmt(value)); }, [value, precision]);

  const commit = () => {
    const raw = String(draft).trim();
    if (raw === "" || raw === "-" || raw === ".") { setDraft(fmt(value)); return; }
    const parsed = precision > 0 ? parseFloat(raw) : parseInt(raw, 10);
    if (Number.isNaN(parsed)) { setDraft(fmt(value)); return; }
    let v = parsed;
    if (min !== undefined && min !== null) v = Math.max(Number(min), v);
    if (max !== undefined && max !== null) v = Math.min(Number(max), v);
    v = round(v);
    setDraft(fmt(v));
    if (v !== value) onChange(v);
  };

  return (
    <input
      type="number"
      value={draft}
      min={min}
      max={max}
      step={step}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      className={className}
      {...rest}
    />
  );
}
